import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAstrologerDetail } from "../../services/astrologerService";
import { motion } from "framer-motion";
import { FiMessageCircle, FiStar, FiChevronLeft, FiMapPin, FiCheckCircle, FiShoppingBag } from "react-icons/fi";

export default function AstrologerDetail() {
    const { id, astrologerId } = useParams();
    const navigate = useNavigate();
    const [astrologer, setAstrologer] = useState(null);

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                const data = await getAstrologerDetail(astrologerId);
                setAstrologer(data);
            } catch (error) {
                console.error("Astrologer detail fetch error:", error);
                navigate(`/${id}/module-list`);
            }
        };
        fetchDetail();
    }, [astrologerId, id, navigate]);

    if (!astrologer) return null;

    return (
        <div className="w-full h-screen min-h-screen flex flex-col items-center bg-[#F8F9FB] overflow-hidden select-none">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col w-full h-full sm:max-w-[390px] bg-white sm:rounded-[40px] shadow-2xl relative overflow-hidden"
            >
                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
                    {/* Top Image Section with Back Button */}
                    <div className="relative w-full h-[320px] flex-shrink-0 rounded-b-[40px] overflow-hidden shadow-sm">
                        <img
                            src={astrologer.avatar || `https://ui-avatars.com/api/?name=${astrologer.name.replace(
                                " ",
                                "+"
                            )}&background=3DA7B5&color=fff&size=512&bold=true`}
                            alt={astrologer.name}
                            className="w-full h-full object-cover object-top transform scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10"></div>

                        {/* Simple Back Button */}
                        <button
                            onClick={() => navigate(-1)}
                            className="absolute top-6 left-6 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-all z-20 shadow-lg"
                        >
                            <FiChevronLeft className="w-6 h-6" />
                        </button>

                        {/* Market Shortcut */}
                        <button
                            onClick={() => navigate(`/${id}/store`)}
                            className="absolute top-6 right-6 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-all z-20 shadow-lg active:scale-90"
                        >
                            <FiShoppingBag className="w-5 h-5" />
                        </button>

                        <div className="absolute bottom-8 left-6 right-6 z-10 text-left">
                            <div className="inline-block px-4 py-1.5 mb-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm">
                                {astrologer.archetype}
                            </div>
                            <h2 className="text-3xl font-black text-white leading-tight flex items-center gap-2">
                                {astrologer.name}
                                <FiCheckCircle className="text-[#3DA7B5] w-6 h-6 fill-white" />
                            </h2>
                            <p className="text-gray-200 text-sm font-medium mt-1.5 flex items-center gap-1.5 opacity-90">
                                <FiMapPin className="w-4 h-4 text-[#3DA7B5]" />
                                {astrologer.city_background || "Global"}
                            </p>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="bg-white relative z-20 px-6 pt-8 pb-10 text-left w-full">
                        {/* Approach Area */}
                        <div className="mb-8">
                            <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                Yaklaşım & Odak
                                <div className="flex-1 h-px bg-gray-100"></div>
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {(astrologer.focus_areas || []).map((area, i) => (
                                    <span
                                        key={i}
                                        className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-gray-50 to-gray-100 text-gray-600 border border-gray-200 shadow-sm"
                                    >
                                        {area}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Biography */}
                        <div className="mb-8 p-5 bg-[#F4F9F9] rounded-2xl border border-[#E0F0F1] relative">
                            <div className="absolute top-0 right-5 transform -translate-y-1/2 text-4xl text-[#3DA7B5] opacity-20 font-serif">"</div>
                            <h3 className="text-[11px] font-black text-[#3DA7B5] uppercase tracking-widest mb-2">
                                Hakkında
                            </h3>
                            <p className="text-gray-700 text-[13px] leading-relaxed font-medium italic">
                                {astrologer.biography}
                            </p>
                        </div>

                        {/* Personality */}
                        <div className="mb-4">
                            <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                Kişilik Özellikleri
                                <div className="flex-1 h-px bg-gray-100"></div>
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {(astrologer.personality_traits || []).map((trait, i) => (
                                    <span
                                        key={i}
                                        className="bg-white text-gray-500 px-3.5 py-1.5 rounded-full text-xs font-semibold border border-gray-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)]"
                                    >
                                        #{trait}
                                    </span>
                                ))}
                            </div>
                        </div>
                        {/* Action Buttons - Moved inside scroll flow */}
                        <div className="mt-8 flex flex-col gap-3">
                            <button className="w-full bg-gradient-to-r from-[#2a8a96] to-[#3DA7B5] text-white font-bold py-4 px-5 rounded-2xl shadow-lg shadow-[#3DA7B5]/25 hover:opacity-90 transition-all flex items-center justify-between group transform active:scale-[0.98] relative overflow-hidden">
                                <div className="absolute inset-0 bg-white/20 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                                <span className="flex items-center gap-3 relative z-10 text-[15px]">
                                    <FiStar className="w-5 h-5" />
                                    Doğum Haritamı Yorumlat
                                </span>
                                <div className="relative z-10 flex items-center gap-1.5 bg-black/10 px-3 py-1.5 rounded-xl border border-white/20">
                                    <span className="text-xs font-black">{astrologer.tokens_birth_chart}</span>
                                    <span className="text-[10px] font-bold uppercase opacity-80">Token</span>
                                </div>
                            </button>

                            <button className="w-full bg-gray-900 text-white font-bold py-4 px-5 rounded-2xl shadow-lg shadow-gray-900/20 hover:bg-gray-800 transition-all flex items-center justify-between group transform active:scale-[0.98]">
                                <span className="flex items-center gap-3 text-[14px]">
                                    <FiMessageCircle className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                                    İletişime Geç <span className="text-gray-400 font-medium ml-1 text-xs">(24 Saat)</span>
                                </span>
                                <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-xl border border-white/10">
                                    <span className="text-xs font-black text-gray-100">{astrologer.tokens_chat}</span>
                                    <span className="text-[10px] font-bold uppercase text-gray-400">Token</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
